import {
  NumericStepper,
  Box,
  Flex,
  Stack,
  Text,
  PrimaryButton,
  SystemProps,
  ensurePlainTextFormat,
} from "flicket-ui";
import { Badge } from "~components";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import PriceWithFee from "~components/reservation/common/common.PriceWithFee";
import { useRouter } from "next/router";
import { DetailsModal } from "../DetailsModal/DetailsModal";
import { addQueryParams, removeQueryParams } from "~lib/helpers/queryParams";
import PriceOrFree from "../common/PriceOrFree/PriceOrFree";
import { useState } from "react";
import { pick } from "@styled-system/props";
import { useIsMobile } from "~hooks";
import { TicketAvailabilityStatus } from "~graphql/sdk";
import { ImageSlider } from "~features/reservation/non-seated/ImageSlider";
import { TextPreviewRender } from "../common/TextRenders/TextRenders";

interface INonSeatedListItem {
  ticket: IReleaseTicket;
  onChange: (value: number, ticketId: string, zoneId: string) => void;
  quantity: number;
  showHiddenFees: boolean;
  showHighDemandBadge: boolean;
  onClick?: () => void;
  isFirstItem: boolean;
  isLastItem: boolean;
}

export const ExperienceSalesTicketTypeItem = ({
  ticket,
  onChange,
  quantity,
  showHiddenFees,
  showHighDemandBadge,
  isFirstItem,
  isLastItem,
}: INonSeatedListItem) => {
  const router = useRouter();

  function handleItemClick() {
    const selection = window.getSelection();
    if (selection.toString().length === 0) {
      addQueryParams(router, { modalId: ticket.id });
    }
  }

  function closeModal() {
    removeQueryParams(router, ["modalId"]);
  }

  const hasGalleryItem = ticket.imageGallery?.length > 0;

  const canClickItem =
    hasGalleryItem || ensurePlainTextFormat(ticket.description)?.length > 0;

  const availabilityStatus = getAvailabilityStatus(ticket);

  return (
    <>
      <Card
        id={ticket.id}
        onClick={canClickItem ? handleItemClick : undefined}
        isSelected={quantity > 0}
        hasGalleryItem={hasGalleryItem}
        isLastItem={isLastItem}
        css={`
          &:hover {
            .slider-navigation {
              opacity: 0.8;
            }
          }
        `}
      >
        <Stack
          gap={2}
          direction={["vertical", "vertical", "horizontal"]}
          justifyContent="space-between"
          flex={1}
        >
          {ticket.imageGallery?.length > 0 && (
            <Box>
              <ImageSlider
                imageGallery={ticket.imageGallery}
                aspectRatio="3 / 2"
                navigationSize="sm"
                borderRadius="sm"
                width={{ _: "100%", xs: 196, sm: 260 }}
                imageProps={{
                  objectFit: "cover",
                }}
              />
            </Box>
          )}
          <Box flex={1}>
            <Stack
              gap={3}
              direction="horizontal"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Text variant="header.XS">{ticket.name}</Text>
                <Stack
                  gap={1}
                  alignItems={{ _: "flex-start", sm: "center" }}
                  direction={{ _: "vertical", sm: "horizontal" }}
                >
                  <TicketPriceAndFee
                    ticket={ticket}
                    showFees={showHiddenFees}
                  />
                  {showHighDemandBadge && <Badge>High demand</Badge>}
                </Stack>
                {availabilityStatus === "Unavailable" && (
                  <Text variant="small" color="error" mt="1/2">
                    All tickets are currently in carts. Please try again later.
                  </Text>
                )}
              </Box>
              <Box onClick={(e) => e.stopPropagation()}>
                {availabilityStatus !== "available" && (
                  <Text variant="header.XS" color="N500">
                    {availabilityStatus}
                  </Text>
                )}

                {availabilityStatus === "available" && (
                  <NumericStepper
                    value={quantity}
                    onChange={(value: number) =>
                      onChange(value, ticket.id, ticket.zoneId)
                    }
                    min={ticket.minPurchaseQuantity}
                    max={ticket.maxPurchaseQuantity}
                  />
                )}
              </Box>
            </Stack>
            <Box mt="1/2" pr={{ _: 0, sm: "140px" } as any}>
              <TextPreviewRender
                value={ticket.description}
                lines={2}
                variant="small"
              />
            </Box>
          </Box>
        </Stack>
      </Card>

      <Modal ticketType={ticket} quantity={quantity} onChange={onChange} />
    </>
  );
};

function Card({
  id,
  onClick,
  children,
  isSelected = false,
  hasGalleryItem,
  isLastItem = false,
}: {
  id: string;
  onClick: () => void;
  children: React.ReactNode;
  isSelected: boolean;
  hasGalleryItem: boolean;
  isLastItem?: boolean;
}) {
  return (
    <Box
      id={id}
      borderTop="1px solid"
      borderTopColor={{
        _: hasGalleryItem ? "transparent" : "N200",
        sm: "N200",
      }}
      borderBottom="1px solid"
      borderBottomColor={isLastItem ? "N200" : "transparent"}
      onClick={onClick}
      cursor={onClick ? "pointer" : "auto"}
      css={`
        transition: box-shadow 0.15s;
        -webkit-tap-highlight-color: transparent;
        &:hover {
          .slider-navigation {
            opacity: 0.8;
          }
        }
      `}
    >
      <Flex alignItems="center" py={2} flex={1}>
        {children}
      </Flex>
    </Box>
  );
}

function Modal({
  ticketType,
  quantity,
  onChange,
}: {
  ticketType: IReleaseTicket;
  quantity: number;
  onChange: INonSeatedListItem["onChange"];
}) {
  const isMobile = useIsMobile("sm");

  const router = useRouter();
  const [tempQuantity, setTempQuantity] = useState(quantity ?? 1);

  function closeModal() {
    removeQueryParams(router, ["modalId"]);
  }

  function onSubmit() {
    onChange(tempQuantity, ticketType.id, ticketType.zoneId);
    closeModal();
  }

  return (
    <DetailsModal
      isOpen={router?.query?.modalId === ticketType.id}
      close={closeModal}
      title={ticketType.name}
      description={ticketType.description}
      imageGallery={ticketType.imageGallery}
      aspectRatio={"3 / 2"}
      stickySection={({ isSticky }) => (
        <Stack
          px={isSticky ? { _: 2, sm: 4 } : 0}
          gap={3}
          direction="horizontal"
          justifyContent="space-between"
          flex={1}
          alignItems="center"
        >
          <Box>
            <Text as="h1" variant="header.M">
              {ticketType.name}
            </Text>
            <Text variant="regular">
              <PriceOrFree price={ticketType.price} />
            </Text>
          </Box>
        </Stack>
      )}
      footer={
        <TicketTypeQunatitySelect
          ticketType={ticketType}
          quantity={quantity}
          tempQuantity={tempQuantity}
          onChange={setTempQuantity}
          onSubmit={onSubmit}
        />
      }
    />
  );
}

function TicketTypeQunatitySelect({
  ticketType,
  quantity,
  tempQuantity,
  onChange,
  onSubmit,
}: {
  ticketType: IReleaseTicket;
  quantity: number;
  tempQuantity: number;
  onChange: (q: number) => void;
  onSubmit: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonLabel] = useState(
    quantity === undefined ? "Add to order" : "Update order"
  );

  return (
    <>
      <Box>
        <Stack gap={2} alignItems="center">
          <NumericStepper
            value={tempQuantity}
            onChange={(value: number) => onChange(value)}
            min={ticketType.minPurchaseQuantity}
            max={ticketType.maxPurchaseQuantity}
            allowZero={true}
            disableDecrement={tempQuantity === undefined}
          />
          <PrimaryButton
            disabled={isSubmitting}
            w="150px"
            onClick={() => {
              setIsSubmitting(true);
              onSubmit();
            }}
          >
            {buttonLabel}
          </PrimaryButton>
        </Stack>
      </Box>
    </>
  );
}

function TicketPriceAndFee({
  ticket,
  showFees,
  ...props
}: {
  showFees: boolean;
  ticket: IReleaseTicket;
} & SystemProps) {
  return (
    <Box {...pick(props)}>
      <Text as="span" variant="regular">
        <PriceOrFree price={ticket.price} />
      </Text>
      {showFees && (
        <Text as="span" variant="small">
          {" "}
          <PriceWithFee.Fee showFees={true} ticket={ticket} />
        </Text>
      )}
    </Box>
  );
}

function getAvailabilityStatus(ticket: IReleaseTicket) {
  if (
    ticket.availabilityStatus === TicketAvailabilityStatus.AllocationExhausted
  ) {
    return "Unavailable";
  }

  if (
    ticket.availabilityStatus === TicketAvailabilityStatus.Soldout ||
    ticket.isSoldOut
  ) {
    return "Sold out";
  }

  return "available";
}
